import { useEffect, useState } from 'react';
import { FormField } from '@/components/ui/FormField';
import { FormSelect } from '@/components/ui/FormSelect';
import { TextInput } from '@/components/ui/TextInput';
import { Toggle } from '@/components/ui/Toggle';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { adminApi } from '@/shared/api/adminApi';
import type {
  CallerIdStrategy,
  DialStrategy,
  DialerSettings,
  HubAutomation,
  QuickAction,
  RetryStep,
  RetryLadderMap,
  RetryLadderOutcome,
  UnansweredOutcome,
} from '@/shared/api/adminApi';
import {
  CALLER_ID_STRATEGY_LABEL,
  DIAL_STRATEGIES,
  UNANSWERED_OUTCOMES,
  UNANSWERED_OUTCOME_LABEL,
  emptyQuickAction,
  emptyRetryStep,
  strategyMeta,
  DIALER_TOGGLE_DEFAULTS,
} from './dialerSettings';

export interface DialerSettingsFormProps {
  /** Current edited draft. */
  value: Partial<DialerSettings>;
  /** Emits the merged draft on any change. */
  onChange: (next: Partial<DialerSettings>) => void;
  /**
   * When set, unfilled fields render their inherited tenant default as a hint
   * ("ברירת מחדל: …") — used in the per-campaign form. Absent for the tenant
   * defaults form, where every field is required.
   */
  inherited?: Partial<DialerSettings>;
}

const NOT_RECOMMENDED_BADGE = (
  <span
    style={{
      fontSize: 'var(--t-xs)',
      color: 'var(--warn)',
      border: '1px solid var(--warn)',
      borderRadius: 'var(--r-pill)',
      padding: '1px 8px',
      whiteSpace: 'nowrap',
    }}
  >
    לא מומלץ
  </span>
);

export function DialerSettingsForm({ value, onChange, inherited }: DialerSettingsFormProps) {
  const set = (patch: Partial<DialerSettings>) => onChange({ ...value, ...patch });
  const isCampaign = !!inherited;
  const defaultHint = (key: keyof DialerSettings, fmt?: (v: unknown) => string) => {
    if (!inherited) return undefined;
    const v = inherited[key];
    if (v === undefined || v === null) return undefined;
    return `ברירת מחדל: ${fmt ? fmt(v) : String(v)}`;
  };

  const strategy = value.dial_strategy ?? (isCampaign ? undefined : 'hot_blend_newest');
  const meta = strategyMeta(strategy);

  const callerStrategy: CallerIdStrategy | undefined =
    value.caller_id_strategy ?? (isCampaign ? undefined : 'fixed');

  // הסולם יכול להיות רשימה שטוחה (אחיד) או מפה לפי-סטטוס. זיהוי הצורה קובע את המצב.
  const ladderRaw = value.retry_ladder;
  const perStatus = !!ladderRaw && !Array.isArray(ladderRaw);
  const outcomes: UnansweredOutcome[] = value.unanswered_outcomes ?? [];

  return (
    <div className="stack">
      {/* Lines per agent */}
      <FormField
        label="קווים לכל נציג"
        hint={defaultHint('lines_per_agent') ?? 'כל נציג פנוי מחייג למספר לקוחות בו-זמנית; הראשון שעונה נשאר, השאר חוזרים לתור.'}
      >
        <TextInput
          type="number"
          min={1}
          max={10}
          dir="ltr"
          value={value.lines_per_agent === undefined ? '' : String(value.lines_per_agent)}
          placeholder={isCampaign && inherited?.lines_per_agent !== undefined ? String(inherited.lines_per_agent) : undefined}
          onChange={(e) => set({ lines_per_agent: e.target.value === '' ? undefined : Math.max(1, Math.min(10, Number(e.target.value) || 1)) })}
        />
      </FormField>

      {/* Dial strategy */}
      <FormField label="שיטת חיוג">
        <FormSelect
          value={strategy ?? ''}
          onChange={(e) => set({ dial_strategy: (e.target.value || undefined) as DialStrategy | undefined })}
        >
          {isCampaign && <option value="">שימוש בברירת המחדל של העסק</option>}
          {DIAL_STRATEGIES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </FormSelect>
      </FormField>
      {meta && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', padding: 10 }}>
          {meta.discouraged && NOT_RECOMMENDED_BADGE}
          <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>{meta.description}</p>
        </div>
      )}

      {/* Wrap-up toggles */}
      <div style={{ display: 'grid', gap: 8 }}>
        <Toggle
          checked={value.wrapup_required ?? inherited?.wrapup_required ?? DIALER_TOGGLE_DEFAULTS.wrapup_required}
          onChange={(v) => set({ wrapup_required: v })}
          label="חובה לתעד בסוף שיחה"
        />
        <Toggle
          checked={value.wrapup_blocks_next ?? inherited?.wrapup_blocks_next ?? DIALER_TOGGLE_DEFAULTS.wrapup_blocks_next}
          onChange={(v) => set({ wrapup_blocks_next: v })}
          label="השיחה הבאה רק אחרי תיעוד"
        />
        <Toggle
          checked={value.auto_dial_next ?? inherited?.auto_dial_next ?? DIALER_TOGGLE_DEFAULTS.auto_dial_next}
          onChange={(v) => set({ auto_dial_next: v })}
          label="חיוג אוטומטי לשיחה הבאה"
        />
        <Toggle
          checked={value.transfer_allowed ?? inherited?.transfer_allowed ?? DIALER_TOGGLE_DEFAULTS.transfer_allowed}
          onChange={(v) => set({ transfer_allowed: v })}
          label="אפשר לנציגים להעביר שיחות"
        />
        <Toggle
          checked={value.amd_enabled ?? inherited?.amd_enabled ?? DIALER_TOGGLE_DEFAULTS.amd_enabled}
          onChange={(v) => set({ amd_enabled: v })}
          label="זהה מזכירה אלקטרונית (מוסיף 1–2 שניות שקט בכל שיחה שנענתה)"
        />
      </div>

      {/* Max unanswered per day */}
      <FormField label="מקסימום ניסיונות ללא מענה ביום" hint={defaultHint('max_unanswered_per_day', (v) => (Number(v) === 0 ? 'ללא הגבלה' : String(v))) ?? '0 = ללא הגבלה'}>
        <TextInput
          type="number"
          min={0}
          dir="ltr"
          value={value.max_unanswered_per_day === undefined ? '' : String(value.max_unanswered_per_day)}
          placeholder={isCampaign && inherited?.max_unanswered_per_day !== undefined ? String(inherited.max_unanswered_per_day) : undefined}
          onChange={(e) => set({ max_unanswered_per_day: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value) || 0) })}
        />
      </FormField>

      {/* Retry ladder — סולם אחיד או לפי-סטטוס */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>סולם חיוג</strong>
          <div style={{ display: 'inline-flex', gap: 4 }}>
            <button
              type="button"
              className={`btn ${!perStatus ? 'primary' : 'ghost'}`}
              style={{ padding: '3px 9px', fontSize: 'var(--t-xs)' }}
              onClick={() => { if (perStatus) set({ retry_ladder: flattenLadder(ladderRaw as RetryLadderMap) }); }}
            >
              סולם אחיד
            </button>
            <button
              type="button"
              className={`btn ${perStatus ? 'primary' : 'ghost'}`}
              style={{ padding: '3px 9px', fontSize: 'var(--t-xs)' }}
              onClick={() => { if (!perStatus) set({ retry_ladder: { default: (ladderRaw as RetryStep[]) ?? [] } }); }}
            >
              סולם לפי סטטוס
            </button>
          </div>
        </div>

        {!perStatus ? (
          <RetryStepsEditor
            steps={(ladderRaw as RetryStep[]) ?? []}
            emptyHint={`אין שלבים${isCampaign ? ' — יחול סולם ברירת המחדל של העסק' : ''}.`}
            onChange={(steps) => set({ retry_ladder: steps })}
          />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', margin: 0 }}>
              סולם נפרד לכל תוצאת שיחה; "ברירת מחדל" חל על תוצאות ללא סולם ייעודי.
            </p>
            {RETRY_LADDER_OUTCOMES.map((oc) => (
              <div key={oc}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)' }}>{RETRY_LADDER_OUTCOME_LABEL[oc]}</strong>
                </div>
                <RetryStepsEditor
                  steps={(ladderRaw as RetryLadderMap)[oc] ?? []}
                  emptyHint="אין שלבים לתוצאה זו."
                  onChange={(steps) => {
                    const map = { ...(ladderRaw as RetryLadderMap) };
                    if (steps.length) map[oc] = steps; else delete map[oc];
                    set({ retry_ladder: map });
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Caller ID strategy */}
      <FormField label="בחירת מספר מזוהה (Caller ID)">
        <FormSelect
          value={callerStrategy ?? ''}
          onChange={(e) => set({ caller_id_strategy: (e.target.value || undefined) as CallerIdStrategy | undefined })}
        >
          {isCampaign && <option value="">שימוש בברירת המחדל של העסק</option>}
          {(Object.keys(CALLER_ID_STRATEGY_LABEL) as CallerIdStrategy[]).map((s) => (
            <option key={s} value={s}>{CALLER_ID_STRATEGY_LABEL[s]}</option>
          ))}
        </FormSelect>
      </FormField>
      {callerStrategy === 'agent' && (
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: -6 }}>
          כל שיחה בקמפיין תצא עם המספר היוצא המשויך לנציג שמחייג (הגדרות ← נציגים).
          לנציג בלי מספר משויך — תצא עם המספר הקבוע של הקמפיין.
        </div>
      )}
      {callerStrategy === 'rotate' && (
        <FormField label="החלף מספר אחרי כמה שיחות ללא מענה" hint={defaultHint('rotate_threshold')}>
          <TextInput
            type="number"
            min={1}
            dir="ltr"
            value={value.rotate_threshold === undefined ? '' : String(value.rotate_threshold)}
            placeholder={isCampaign && inherited?.rotate_threshold !== undefined ? String(inherited.rotate_threshold) : undefined}
            onChange={(e) => set({ rotate_threshold: e.target.value === '' ? undefined : Math.max(1, Number(e.target.value) || 1) })}
          />
        </FormField>
      )}

      {/* Unanswered outcomes */}
      <div>
        <strong style={{ fontSize: 'var(--t-sm)', display: 'block', marginBottom: 6 }}>מה נחשב ללא מענה</strong>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {UNANSWERED_OUTCOMES.map((o) => {
            const current = value.unanswered_outcomes ?? inherited?.unanswered_outcomes ?? [];
            const checked = current.includes(o);
            return (
              <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const base = value.unanswered_outcomes ?? inherited?.unanswered_outcomes ?? [];
                    const next = e.target.checked ? [...base, o] : base.filter((x) => x !== o);
                    set({ unanswered_outcomes: next });
                  }}
                />
                {UNANSWERED_OUTCOME_LABEL[o]}
              </label>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <QuickActionsEditor
        value={value.quick_actions ?? []}
        onChange={(next) => set({ quick_actions: next })}
        inheritedNote={isCampaign ? 'ריק = שימוש בפעולות ברירת המחדל של העסק' : undefined}
      />
    </div>
  );
}

// תוויות עבריות לתוצאות הסולם לפי-סטטוס (כולל ברירת מחדל).
const RETRY_LADDER_OUTCOMES: RetryLadderOutcome[] = ['no_answer', 'busy', 'failed', 'machine', 'default'];
const RETRY_LADDER_OUTCOME_LABEL: Record<RetryLadderOutcome, string> = {
  no_answer: 'לא ענה',
  busy: 'תפוס',
  failed: 'נכשל',
  machine: 'משיבון',
  default: 'ברירת מחדל',
};

// שיטוח מפת-סטטוסים חזרה לרשימה אחת בעת מעבר ל"סולם אחיד" (מעדיף default).
function flattenLadder(map: RetryLadderMap): RetryStep[] {
  if (map.default?.length) return map.default;
  for (const oc of RETRY_LADDER_OUTCOMES) {
    if (map[oc]?.length) return map[oc]!;
  }
  return [];
}

/** עורך שלבי-סולם ({attempts, interval_min}) — משותף למצב אחיד ולכל סטטוס. */
function RetryStepsEditor({ steps, emptyHint, onChange }: {
  steps: RetryStep[];
  emptyHint: string;
  onChange: (steps: RetryStep[]) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
        <button type="button" className="btn" onClick={() => onChange([...steps, emptyRetryStep()])}>
          + הוסף שלב
        </button>
      </div>
      {steps.length === 0 ? (
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{emptyHint}</div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {steps.map((step, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6, alignItems: 'end' }}>
              <FormField label="כמות ניסיונות">
                <TextInput
                  type="number" min={1} dir="ltr"
                  value={String(step.attempts)}
                  onChange={(e) => {
                    const next = [...steps];
                    next[i] = { ...step, attempts: Math.max(1, Number(e.target.value) || 1) };
                    onChange(next);
                  }}
                />
              </FormField>
              <FormField label="מרווח בדקות">
                <TextInput
                  type="number" min={0} dir="ltr"
                  value={String(step.interval_min)}
                  onChange={(e) => {
                    const next = [...steps];
                    next[i] = { ...step, interval_min: Math.max(0, Number(e.target.value) || 0) };
                    onChange(next);
                  }}
                />
              </FormField>
              <button
                type="button" className="btn danger" aria-label="הסר שלב"
                onClick={() => onChange(steps.filter((_, j) => j !== i))}
              >
                <Icon name="trash" size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickActionsEditor({
  value,
  onChange,
  inheritedNote,
}: {
  value: QuickAction[];
  onChange: (next: QuickAction[]) => void;
  inheritedNote?: string;
}) {
  const update = (i: number, patch: Partial<QuickAction>) => {
    const next = [...value];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  // גלריית האוטומציות של המרכזיה — נטענת פעם אחת, רק כשיש פעולה מסוג אוטומציה
  // (כדי להציג את שמה) או כשפותחים את הגלריה.
  const [automations, setAutomations] = useState<HubAutomation[] | null>(null);
  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const needsAutomations = pickerFor !== null || value.some((q) => q.type === 'automation');
  useEffect(() => {
    if (!needsAutomations || automations !== null) return;
    void adminApi.hubAutomations().then(setAutomations).catch(() => setAutomations([]));
  }, [needsAutomations, automations]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>פעולות מהירות בכרטיס השיחה</strong>
        <button type="button" className="btn" onClick={() => onChange([...value, emptyQuickAction()])}>
          + הוסף פעולה
        </button>
      </div>
      {value.length === 0 ? (
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          {inheritedNote ?? 'אין עדיין פעולות. הוסף כפתור לקישור חיצוני או להפעלת אוטומציה.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {value.map((qa, i) => (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, display: 'grid', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px auto', gap: 8, alignItems: 'end' }}>
                <FormField label="תווית">
                  <TextInput value={qa.label} onChange={(e) => update(i, { label: e.target.value })} />
                </FormField>
                <FormField label="סוג">
                  <FormSelect value={qa.type} onChange={(e) => update(i, { type: e.target.value as QuickAction['type'] })}>
                    <option value="url">קישור</option>
                    <option value="automation">אוטומציה</option>
                  </FormSelect>
                </FormField>
                <button
                  type="button"
                  className="btn danger"
                  aria-label="הסר פעולה"
                  onClick={() => onChange(value.filter((_, j) => j !== i))}
                >
                  <Icon name="trash" size={16} />
                </button>
              </div>
              {qa.type === 'url' ? (
                <FormField label="כתובת" hint="ניתן להשתמש ב-{{phone}} {{name}} {{call_id}} {{agent}}">
                  <TextInput
                    dir="ltr"
                    value={qa.url ?? ''}
                    placeholder="https://crm.example.com/lead?phone={{phone}}"
                    onChange={(e) => update(i, { url: e.target.value })}
                  />
                </FormField>
              ) : (
                <FormField label="אוטומציה">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn" onClick={() => setPickerFor(i)}>
                      <Icon name="bolt" size={14} color="var(--accent)" />
                      {(() => {
                        if (qa.automation_id === undefined) return 'בחר אוטומציה מהגלריה…';
                        const found = automations?.find((a) => a.id === qa.automation_id);
                        return found ? found.name : `אוטומציה #${qa.automation_id}`;
                      })()}
                    </button>
                    {qa.automation_id !== undefined && automations !== null && !automations.some((a) => a.id === qa.automation_id) && (
                      <StatusBadge tone="warn" size="sm">האוטומציה לא נמצאה — בחר אחרת</StatusBadge>
                    )}
                  </div>
                </FormField>
              )}
            </div>
          ))}
        </div>
      )}

      <AutomationGalleryModal
        isOpen={pickerFor !== null}
        automations={automations}
        selectedId={pickerFor !== null ? value[pickerFor]?.automation_id : undefined}
        onClose={() => setPickerFor(null)}
        onPick={(a) => {
          if (pickerFor !== null) {
            // אם לפעולה עוד אין תווית — שם האוטומציה הוא ברירת מחדל טבעית לכפתור.
            const patch: Partial<QuickAction> = { automation_id: a.id };
            if (!value[pickerFor]?.label) patch.label = a.name;
            update(pickerFor, patch);
          }
          setPickerFor(null);
        }}
      />
    </div>
  );
}

const TRIGGER_TYPE_LABEL: Record<HubAutomation['trigger_type'], string> = {
  event: 'אירוע במערכת',
  inbound: 'Webhook נכנס',
  schedule: 'מתוזמן',
};

/** גלריית כל האוטומציות של המרכזיה — בחירה בלחיצה במקום הקלדת מזהה. */
function AutomationGalleryModal({ isOpen, automations, selectedId, onClose, onPick }: {
  isOpen: boolean;
  automations: HubAutomation[] | null;
  selectedId?: number;
  onClose: () => void;
  onPick: (a: HubAutomation) => void;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="בחירת אוטומציה" size="lg">
      {automations === null ? (
        <div style={{ color: 'var(--muted)', padding: 12 }}>טוען אוטומציות…</div>
      ) : automations.length === 0 ? (
        <div style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', color: 'var(--muted)', fontSize: 'var(--t-sm)', lineHeight: 1.6 }}>
          אין עדיין אוטומציות במרכזיה הזאת.
          <br />
          יוצרים אוטומציה במסך <strong>אינטגרציות ← אוטומציות</strong>, ואז בוחרים אותה כאן ככפתור מהיר בכרטיס השיחה.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {automations.map((a) => {
            const selected = a.id === selectedId;
            return (
              <button
                key={a.id}
                type="button"
                className={`mode-card${selected ? ' selected' : ''}`}
                style={{ padding: 14, gap: 8, ...(selected ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' } : {}) }}
                onClick={() => onPick(a)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ display: 'inline-flex', padding: 7, background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 'var(--r-2)' }}>
                    <Icon name="bolt" size={15} />
                  </span>
                  <StatusBadge tone={a.active ? 'ok' : 'neutral'} dot size="sm">{a.active ? 'פעילה' : 'כבויה'}</StatusBadge>
                </div>
                <div style={{ fontSize: 'var(--t-md)', fontWeight: 700, lineHeight: 1.3 }}>{a.name}</div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span>{TRIGGER_TYPE_LABEL[a.trigger_type] ?? a.trigger_type}</span>
                  <span>·</span>
                  <span>{a.actions.length} פעולות</span>
                  {a.runs_count > 0 && <><span>·</span><span>{a.runs_count} הרצות</span></>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
